// External libraries
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { GeoId } from '../types/geo.types';

// Components
import { cifState, markerFilterDataState, POICategories, spinnerState, userCurrencyState } from "../states";

// Utilities
import { useFetchWrapper } from '../helpers';
import { APIS, CATEGORY_ORDER_IN_DROPDOWN } from '../constants';


const useCIFService = () => {
    const fetchWrapper = useFetchWrapper();
    const setDashboardData = useSetRecoilState(cifState);
    const setPoiCategories = useSetRecoilState(POICategories);
    const setMarkerFilterData = useSetRecoilState(markerFilterDataState);
    const SetSpinner = useSetRecoilState(spinnerState);
    const selCurrency = useRecoilValue(userCurrencyState);

    const normalizeGeoId = (geoId: any): GeoId =>
        geoId == 1 || geoId === '1' ? 'EW-IN-IND-22-0' : geoId;

    const normalizePayload = (payload: any) => {
        if (!payload) return payload;
        const result = { ...payload };
        if (result.geoId != null) result.geoId = normalizeGeoId(result.geoId);
        if (result.geocode != null) result.geocode = normalizeGeoId(result.geocode);
        if (result.geoCode != null) result.geoCode = normalizeGeoId(result.geoCode);
        if (result.parentgeoId != null) result.parentgeoId = normalizeGeoId(result.parentgeoId);
        return result;
    };

    const getInOutFlowData = (geoCode: GeoId) => {
        geoCode = normalizeGeoId(geoCode);
        //SetSpinner(prev => ({ ...prev, count: prev.count + 1 }));
        const currency = localStorage.getItem('currency');
        return fetchWrapper.get(`${APIS.CIF.GET_INOUTFLOW_DATA}?geo-code=${geoCode}&currency=${currency}`).then(data => {
            setDashboardData(prevCIFData => ({
                ...prevCIFData,
                inOutFlowData: data
            }));
            //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        }).catch(error => {
            //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        });
    }

    const getEHGrowthData = (geoCode: GeoId) => {
        geoCode = normalizeGeoId(geoCode);
        // //SetSpinner(prev => ({ ...prev, count: prev.count + 1 }));
        const currency = localStorage.getItem('currency');
        return fetchWrapper.get(`${APIS.CIF.GET_EH_GROWTH_DATA}?geo-code=${geoCode}&currency=${currency}`).then(data => {
            setDashboardData(prevCIFData => ({
                ...prevCIFData,
                ehGrowthGraphData: data
            }));
            // //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        }).catch(error => {
            // //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        });
    }

    const getMetricBreakdownData = (geoCode: GeoId) => {
        geoCode = normalizeGeoId(geoCode);
        // //SetSpinner(prev => ({ ...prev, count: prev.count + 1 }));
        const currency = localStorage.getItem('currency');
        return fetchWrapper.get(`${APIS.CIF.GET_METRIC_BREAKDOWN}?geo-code=${geoCode}&currency=${currency}`).then(data => {
            setDashboardData(prevCIFData => ({
                ...prevCIFData,
                metricBreakdownInfo: data
            }));
            // //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        }).catch(error => {
            // //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        });
    }

    const getGeoSpecificData = (geoCode: GeoId) => {
        geoCode = normalizeGeoId(geoCode);
        // //SetSpinner(prev => ({ ...prev, count: prev.count + 1 }));
        const currency = localStorage.getItem('currency');
        return fetchWrapper.get(`${APIS.CIF.GET_GEO_SPECIFIC_DATA}?geo-code=${geoCode}&currency=${currency}`).then(data => {
            setDashboardData(prevCIFData => ({
                ...prevCIFData,
                geoInfo: data
            }));
            // //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        }).catch(error => {
            // //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        });
    }

    const getCoreSolutionsGraphData = (geoCode: GeoId) => {
        geoCode = normalizeGeoId(geoCode);
        //SetSpinner(prev => ({ ...prev, count: prev.count + 1 }));
        const currency = localStorage.getItem('currency');
        return fetchWrapper.get(`${APIS.CIF.GET_CORE_SOLUTIONS_DATA}?geo-code=${geoCode}&currency=${currency}`).then(data => {
            setDashboardData(prevCIFData => ({
                ...prevCIFData,
                coreSolutionsData: data
            }));
            //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        }).catch(error => {
            //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        });
    }

    const sendEmail = (payload: any) => {
        return fetchWrapper.post(APIS.CIF.SEND_EMAIL, payload);
    }

    const getPOICategoryList = async () => {
        //SetSpinner(prev => ({ ...prev, count: prev.count + 1 }));
        try {
            const response = await fetchWrapper.get(`${APIS.CIF.GET_POI_CATEGORIES}`);
            const catData = response.sort((a: any, b: any) => CATEGORY_ORDER_IN_DROPDOWN.indexOf(a.name) - CATEGORY_ORDER_IN_DROPDOWN.indexOf(b.name));
            setPoiCategories(catData);
            //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        } catch (error) {
            //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        }
    }

    const getMarkerFilters = async (geocode: GeoId) => {
        geocode = normalizeGeoId(geocode);
        try {
            const response: any[] = await fetchWrapper.get(
                `${APIS.CIF.GET_MARKER_FILTERS}?geocode=${geocode}&currency=${selCurrency}`
            );
            const data = response.reduce(
                (acc: Record<string, any>, item: any) => ({ ...acc, ...item }),
                {} as Record<string, { categories: any[]; filters: any[] }>
            );
            setMarkerFilterData(data);
            if (data['PoI']?.categories?.length > 0) {
                const sorted = [...data['PoI'].categories].sort(
                    (a: any, b: any) =>
                        CATEGORY_ORDER_IN_DROPDOWN.indexOf(a.name) - CATEGORY_ORDER_IN_DROPDOWN.indexOf(b.name)
                );
                setPoiCategories(sorted);
            }
        } catch (error) {}
    };

    const getBasicCif = (geoCode: GeoId) => {
        geoCode = normalizeGeoId(geoCode);
        return fetchWrapper.get(`${APIS.CIF.GET_BASIC_CIF}?geo-code=${geoCode}&currency=${selCurrency}`);
    }

    const getCifCtvData = (geoCode: GeoId) => {
        geoCode = normalizeGeoId(geoCode);
        return fetchWrapper.get(`${APIS.CIF.GET_CIF_CTV}?geo-code=${geoCode}`);
    }

    const getCifPoiData = (payload: any) => {
        return fetchWrapper.post(APIS.CIF.GET_POI_CAT_DETAILS, normalizePayload(payload));
    }

    const getPoiByYear = (payload: any) => {
        return fetchWrapper.post(APIS.CIF.GET_POI_BY_YEAR, normalizePayload(payload));
    }

    const getMyPresenceConfig = (geoCode: GeoId) => {
        geoCode = normalizeGeoId(geoCode);
        return fetchWrapper.get(`${APIS.CIF.GET_MY_PRESENCE_CONFIG}?geocode=${geoCode}`);
    }

    const getMyPresence = (payload: any) => {
        return fetchWrapper.post(APIS.CIF.GET_MBP_V2, normalizePayload(payload));
    }

    const getOverlayFilters = (geoCode: GeoId, subLevel: number | null) => {
        geoCode = normalizeGeoId(geoCode);
        return fetchWrapper.get(`${APIS.CIF.GET_OVERLAY_FILTERS}?geocode=${geoCode}${subLevel ? `&sub_level=${subLevel}` : ''}`);
    }

    const getAllFilterSliders = async (overlays: any[], geoCode: GeoId, currency: string, subLevel: number | null) => {
        geoCode = normalizeGeoId(geoCode);
        const promises = overlays
            .filter(overlay => !overlay.isdisable)
            .map(overlay =>
                getSliderOverlay(geoCode, overlay.key, currency, subLevel)
                    .then(data => data)
                    .catch(error => {
                        console.error(`Error fetching slider for overlay ${overlay.key}:`, error);
                    })
            );
    
        const results = await Promise.allSettled(promises);
    
        // Flatten the results (since each promise already returns an object with status)
        return results
            .filter(r => r.status === 'fulfilled')
            .map(r => (r as PromiseFulfilledResult<any>).value);
    };

    const getSliderOverlay = (geoCode: GeoId, key: string, currency: string, subLevel: number | null) => {
        geoCode = normalizeGeoId(geoCode);
        return fetchWrapper.get(`${APIS.CIF.GET_SLIDER_DATA}?geocode=${geoCode}&key=${key}&currency=${currency}${subLevel ? `&sub_level=${subLevel}` : ''}`);
    }

    const getFilteredDistricts = (payload: any) => {
        return fetchWrapper.post(APIS.CIF.GET_FILTERED_DISTRICTS, normalizePayload(payload));
    }

    const tagSlider = (key: string[], action: 'add' | 'remove') => {
        return fetchWrapper.post(APIS.CIF.TAG_SLIDER, { key, action });
    }

    const tagMarkerSliders = (keys: string[]) => {
        return fetchWrapper.post(APIS.CIF.TAG_SLIDER, { key: keys, action: 'add' });
    }

     const getAlllocations = () => {
        return fetchWrapper.get(APIS.COMPARE.GET_ALL_LOCATIONS);
    }

    return {
        getInOutFlowData,
        getEHGrowthData,
        getMetricBreakdownData,
        getGeoSpecificData,
        getCoreSolutionsGraphData,
        sendEmail,
        getPOICategoryList,
        getMarkerFilters,
        getBasicCif,
        getCifCtvData,
        getCifPoiData,
        getPoiByYear,
        getMyPresenceConfig,
        getMyPresence,
        getOverlayFilters,
        getSliderOverlay,
        getFilteredDistricts,
        getAllFilterSliders,
        getAlllocations,
        tagSlider,
        tagMarkerSliders,
    }
}

export { useCIFService };